import { birthdaysBetween } from './birthdays';
import { isActiveMember } from './membership';
import { formatDayMonth, formatShortDate, hashString } from './dates';
import { type Permissions, memberInScope } from './permissions';
import { declines, presenceOf } from './presence';
import { PRIVACY_REQUEST_LABEL, PRIVACY_STATUS_LABEL, isOverdue } from './privacy';
import { skippedOf } from './recurrence';
import { type Lookup, eventInfo, roleOfMember, scopeInfo } from './schedule';
import { swapEventLabel } from './swaps';
import type { DomainState, ScheduleNoticeKind, SwapStatus } from './types';
import { FOLLOW_UP_KINDS, STAGE_NEW, labelOf, openFollowUps } from './visitors';

/**
 * Notificações derivadas do estado: conflitos, escalas publicadas, disponibilidade não
 * configurada e ausências — para a equipe; escalas e lembretes — para o membro. Como
 * nascem do estado, somem sozinhas quando a causa é resolvida (o conflito corrigido
 * deixa de notificar). O que fica guardado é só quais já foram lidas.
 */

export type NotificationTone = 'primary' | 'danger' | 'warning' | 'success';
export type NotificationIcon = 'send' | 'alert' | 'user' | 'plane' | 'calendar' | 'swap' | 'cake';

export interface AppNotification {
  id: string;
  category:
    | 'Escala'
    | 'Conflito'
    | 'Disponibilidade'
    | 'Ausência'
    | 'Lembrete'
    | 'Cadastro'
    | 'Privacidade'
    | 'Troca'
    | 'Visitante'
    | 'Aniversário';
  tone: NotificationTone;
  icon: NotificationIcon;
  title: string;
  text: string;
  at: number;
  target:
    | { type: 'event'; id: string }
    | { type: 'member'; id: string }
    | { type: 'registrations' }
    /** "Minha agenda": os pedidos de troca e a situação dos que a pessoa fez. */
    | { type: 'agenda' }
    /** `answer` = responder (Configurações); `own` = a resposta ao próprio pedido. */
    | { type: 'privacy'; scope: 'answer' | 'own' }
    /** Membresia › Visitantes e integração (`overdue` = só os atrasados). */
    | { type: 'visitors'; overdue: boolean }
    /** Membresia › Aniversariantes. */
    | { type: 'birthdays' }
    | null;
}

/**
 * Aniversários de hoje: "Feliz aniversário!" à própria pessoa (qualquer perfil) e, à equipe,
 * os aniversariantes que ela acompanha — todos para o Administrador; para o líder, os do
 * recorte dele. O id leva a data, então a notificação de cada ano é nova.
 */
function birthdayNotifications(
  state: DomainState,
  permissions: Permissions,
  today: string
): AppNotification[] {
  const at = Date.parse(`${today}T08:00:00`);
  const out: AppNotification[] = [];

  for (const { member, turning } of birthdaysBetween(state.members, today, today)) {
    if (member.id === permissions.memberId) {
      out.push({
        id: `bday-self-${today}`,
        category: 'Aniversário',
        tone: 'success',
        icon: 'cake',
        title: 'Feliz aniversário!',
        text: 'Toda a igreja celebra a sua vida hoje. Que Deus abençoe o seu novo ano!',
        at: at + 1,
        target: null,
      });
      continue;
    }

    if (!permissions.isManager || (!permissions.isAdmin && !memberInScope(member, permissions.scope)))
      continue;

    out.push({
      id: `bday-${member.id}-${today}`,
      category: 'Aniversário',
      tone: 'success',
      icon: 'cake',
      title: 'Aniversário hoje',
      text: `${member.name} ${turning ? `completa ${turning} anos` : 'faz aniversário'} hoje. Que tal mandar os parabéns?`,
      at,
      target: { type: 'birthdays' },
    });
  }

  return out;
}

/**
 * Visitantes: a ficha nova que chegou pelo link (sem responsável) para quem gerencia, e
 * os acompanhamentos vencidos ou de hoje para o responsável.
 */
function visitorNotifications(
  state: DomainState,
  permissions: Permissions,
  today: string
): AppNotification[] {
  if (!permissions.manageVisitors) return [];

  const out: AppNotification[] = [];

  for (const v of state.visitors) {
    if (v.origin === 'link' && v.stage === STAGE_NEW && !v.ownerId && !v.anonymized) {
      out.push({
        id: `vis-${v.id}`,
        category: 'Visitante',
        tone: 'primary',
        icon: 'user',
        title: 'Nova ficha de visitante',
        text: `${v.name} deixou o contato pelo link. Defina quem vai acompanhar.`,
        at: v.createdAt,
        target: { type: 'visitors', overdue: false },
      });
    }
  }

  for (const { visitor, followUp, overdue } of openFollowUps(state.visitors, today)) {
    if (followUp.ownerId !== permissions.memberId || followUp.due > today) continue;

    out.push({
      id: `vfu-${followUp.id}-${overdue ? 'late' : 'today'}`,
      category: 'Visitante',
      tone: overdue ? 'danger' : 'warning',
      icon: overdue ? 'alert' : 'calendar',
      title: overdue ? 'Acompanhamento atrasado' : 'Acompanhamento para hoje',
      text: `${labelOf(FOLLOW_UP_KINDS, followUp.kind)} com ${visitor.name}${overdue ? `, prazo ${formatShortDate(followUp.due)}` : ''}.`,
      at: Date.parse(`${followUp.due}T08:00:00`),
      target: { type: 'visitors', overdue },
    });
  }

  return out;
}

/**
 * Pedidos de privacidade: para o Administrador, os que aguardam resposta (vermelho depois
 * do prazo); para quem pediu, a resposta da igreja.
 */
function privacyNotifications(
  state: DomainState,
  permissions: Permissions,
  today: string
): AppNotification[] {
  const out: AppNotification[] = [];
  const nameOf = (id: string) => state.members.find((m) => m.id === id)?.name ?? 'Um membro';

  for (const r of state.privacyRequests) {
    const label = PRIVACY_REQUEST_LABEL[r.kind].title;

    if (permissions.answerPrivacy && r.status === 'open' && r.memberId !== permissions.memberId) {
      const late = isOverdue(r, today);

      out.push({
        id: `priv-${r.id}`,
        category: 'Privacidade',
        tone: late ? 'danger' : 'warning',
        icon: late ? 'alert' : 'user',
        title: late ? 'Pedido de privacidade atrasado' : 'Pedido de privacidade',
        text: `${nameOf(r.memberId)} pediu: ${label.toLowerCase()}. Responda até ${formatShortDate(r.dueDate)}.`,
        at: r.createdAt,
        target: { type: 'privacy', scope: 'answer' },
      });
    }

    if (r.memberId === permissions.memberId && r.status !== 'open' && r.resolvedAt !== null) {
      out.push({
        id: `priv-${r.id}-${r.status}`,
        category: 'Privacidade',
        tone: r.status === 'done' ? 'success' : 'warning',
        icon: 'user',
        title: `Pedido ${PRIVACY_STATUS_LABEL[r.status].toLowerCase()}`,
        text: r.response ? `${label}: ${r.response}` : `${label}: a igreja respondeu ao seu pedido.`,
        at: r.resolvedAt,
        target: { type: 'privacy', scope: 'own' },
      });
    }
  }

  return out;
}

const HOUR = 3_600_000;

/** Momento estável para o que não tem registro de horário (entre 1 h e 3 dias atrás). */
function stableAt(id: string, now: number): number {
  return now - (1 + (hashString(id) % 72)) * HOUR;
}

/**
 * O que é da própria pessoa: o que mudou na escala dela, "Você foi escalado", a
 * confirmação pendente, a próxima escala e os pedidos de troca (os que recebeu e a
 * situação dos que fez).
 */
function personalNotifications(
  state: DomainState,
  permissions: Permissions,
  lookup: Lookup,
  today: string,
  now: number
): AppNotification[] {
  const upcoming = state.events.filter((e) => e.date >= today);
  const out: AppNotification[] = [];

  // Mudou na escala publicada (saiu, trocou de função, voltou): só quem mudou é avisado.
  for (const event of upcoming) {
    const notice = event.published ? event.notices[permissions.memberId] : undefined;

    if (!notice) continue;

    const role = lookup.roles.get(notice.roleId)?.role.name ?? '';
    const when = `${event.title}, ${formatShortDate(event.date)} às ${event.time}`;
    const id = `chg-${event.id}-${notice.kind}-${notice.roleId}`;
    const copy: Record<ScheduleNoticeKind, { title: string; text: string; tone: AppNotification['tone'] }> = {
      removed: { title: 'Você saiu da escala', text: `${role} · ${when}`, tone: 'warning' },
      moved: { title: 'Sua função mudou', text: `Agora em ${role} · ${when}`, tone: 'primary' },
      added: { title: 'Você voltou para a escala', text: `${role} · ${when}`, tone: 'primary' },
    };

    out.push({
      id,
      category: 'Escala',
      icon: 'send',
      ...copy[notice.kind],
      at: stableAt(id, now),
      target: { type: 'event', id: event.id },
    });
  }

  // A data da série excluída sozinha (o culto do feriado): quem estava escalado é avisado.
  const seen = new Set<string>();

  for (const event of state.events) {
    if (seen.has(event.seriesId)) continue;
    seen.add(event.seriesId);

    for (const skipped of skippedOf(event.rule)) {
      if (skipped.date < today || !skipped.notified.includes(permissions.memberId)) continue;

      const id = `skip-${event.seriesId}-${skipped.date}`;

      out.push({
        id,
        category: 'Escala',
        tone: 'warning',
        icon: 'calendar',
        title: 'Evento cancelado',
        text: `${event.title}, ${formatShortDate(skipped.date)} às ${skipped.time} não vai acontecer${
          skipped.reason ? ` · ${skipped.reason}` : ''
        }. Você não precisa mais servir nesta data.`,
        at: skipped.at,
        target: { type: 'agenda' },
      });
    }
  }

  const mine = upcoming.filter((e) => e.published && roleOfMember(e, permissions.memberId, lookup));

  mine.forEach((event, index) => {
    const role = roleOfMember(event, permissions.memberId, lookup);
    const published = state.log.find((l) => l.eventId === event.id && l.text.includes('publicada'));
    const id = `esc-${event.id}`;

    out.push({
      id,
      category: 'Escala',
      tone: 'primary',
      icon: 'send',
      title: 'Você foi escalado',
      text: `${role?.name ?? ''} · ${event.title}, ${formatShortDate(event.date)} às ${event.time}`,
      at: published?.at ?? stableAt(id, now),
      target: { type: 'event', id: event.id },
    });

    if (presenceOf(event, permissions.memberId) === 'pending') {
      out.push({
        id: `rsvp-${event.id}`,
        category: 'Lembrete',
        tone: 'warning',
        icon: 'calendar',
        title: 'Confirme sua presença',
        text: `${role?.name ?? ''} · ${event.title}, ${formatShortDate(event.date)} às ${event.time}. Confirme ou avise que não pode ir.`,
        at: (published?.at ?? stableAt(id, now)) + 1,
        target: { type: 'event', id: event.id },
      });
    }

    if (index === 0) {
      out.push({
        id: `rem-${event.id}`,
        category: 'Lembrete',
        tone: 'warning',
        icon: 'calendar',
        title: 'Sua próxima escala',
        text: `${event.title} · ${formatShortDate(event.date)} às ${event.time}. Confira com quem você serve.`,
        at: now - HOUR / 2,
        target: { type: 'event', id: event.id },
      });
    }
  });

  out.push(...swapNotifications(state, permissions, lookup, today));

  return out;
}

const SWAP_OUTCOME: Partial<
  Record<SwapStatus, { title: (to: string) => string; tone: AppNotification['tone']; icon: NotificationIcon }>
> = {
  leader: { title: (to) => `${to} aceitou a troca`, tone: 'warning', icon: 'calendar' },
  approved: { title: () => 'Troca aprovada', tone: 'success', icon: 'swap' },
  rejected: { title: () => 'Troca recusada pelo líder', tone: 'danger', icon: 'alert' },
  declined: { title: (to) => `${to} recusou a troca`, tone: 'danger', icon: 'alert' },
};

/** Pedidos de troca: o que chegou para a pessoa e o andamento dos que ela fez. */
function swapNotifications(
  state: DomainState,
  permissions: Permissions,
  lookup: Lookup,
  today: string
): AppNotification[] {
  const me = permissions.memberId;
  const nameOf = (id: string) => lookup.members.get(id)?.name ?? 'Um colega';

  return state.events
    .filter((e) => e.published && e.date >= today)
    .flatMap((event) =>
      event.swaps.flatMap((swap): AppNotification[] => {
        const role = lookup.roles.get(swap.roleId)?.role.name ?? '';
        const when = swapEventLabel(event);

        if (swap.toId === me && swap.status === 'peer') {
          return [
            {
              id: `sw-in-${swap.id}`,
              category: 'Troca',
              tone: 'primary',
              icon: 'swap',
              title: `${nameOf(swap.fromId)} pediu uma troca`,
              text: `Você assumiria ${role} em ${when}.`,
              at: swap.at,
              target: { type: 'agenda' },
            },
          ];
        }

        const outcome = swap.fromId === me ? SWAP_OUTCOME[swap.status] : undefined;

        if (!outcome) return [];

        const tails: Partial<Record<SwapStatus, string>> = {
          leader: '. Aguardando aprovação do líder.',
          approved: `. ${nameOf(swap.toId)} assume o seu lugar.`,
        };
        const tail = tails[swap.status] ?? '';

        return [
          {
            id: `sw-out-${swap.id}-${swap.status}`,
            category: 'Troca',
            tone: outcome.tone,
            icon: outcome.icon,
            title: outcome.title(nameOf(swap.toId)),
            text: `${role} · ${when}${tail}`,
            at: swap.at,
            target: { type: 'agenda' },
          },
        ];
      })
    );
}

export function deriveNotifications(
  state: DomainState,
  permissions: Permissions,
  lookup: Lookup,
  today: string,
  now: number = Date.now()
): AppNotification[] {
  const upcoming = state.events.filter((e) => e.date >= today);
  const out: AppNotification[] = [
    ...privacyNotifications(state, permissions, today),
    ...visitorNotifications(state, permissions, today),
    ...birthdayNotifications(state, permissions, today),
  ];

  // As escalas da própria pessoa valem para todos os perfis: a equipe também serve.
  out.push(...personalNotifications(state, permissions, lookup, today, now));

  if (!permissions.isManager) return out.sort((a, b) => b.at - a.at);

  if (permissions.reviewRegistrations) {
    for (const r of state.registrations) {
      if (r.status !== 'pending') continue;

      out.push({
        id: `reg-${r.id}`,
        category: 'Cadastro',
        tone: 'primary',
        icon: 'user',
        title: 'Novo cadastro para aprovar',
        text: `${r.data.name} enviou o cadastro pelo link. Confira os dados e aprove ou recuse.`,
        at: r.submittedAt,
        target: { type: 'registrations' },
      });
    }
  }

  const scope = permissions.scope;
  const inScope = (ministryIds: string[]) =>
    !scope || ministryIds.some((id) => scope.ministryIds.includes(id));

  for (const event of upcoming) {
    if (!inScope(event.ministryIds)) continue;

    const info = scopeInfo(eventInfo(event, lookup), scope?.ministryIds ?? null, scope?.roleIds ?? null);

    const roleIds =
      scope?.roleIds ?? (scope ? info.groups.flatMap((g) => g.functions.map((f) => f.role.id)) : null);

    for (const decline of declines(event, roleIds)) {
      const who = lookup.members.get(decline.memberId)?.name ?? 'Alguém';
      const role = lookup.roles.get(decline.roleId)?.role.name ?? '';

      out.push({
        id: `dec-${event.id}-${decline.memberId}`,
        category: 'Escala',
        tone: 'danger',
        icon: 'alert',
        title: `Vaga reaberta · ${role}`,
        text: `${who} não pode ir a ${event.title}, ${formatShortDate(event.date)}${decline.reason ? `: ${decline.reason}` : '.'}`,
        at: decline.at,
        target: { type: 'event', id: event.id },
      });
    }

    for (const swap of event.swaps) {
      if (swap.status !== 'leader' || !permissions.editRole(swap.roleId)) continue;

      const role = lookup.roles.get(swap.roleId)?.role.name ?? '';
      const name = (id: string) => lookup.members.get(id)?.name ?? 'Alguém';

      out.push({
        id: `sw-l-${swap.id}`,
        category: 'Troca',
        tone: 'primary',
        icon: 'swap',
        title: 'Troca aguardando aprovação',
        text: `${name(swap.fromId)} → ${name(swap.toId)} em ${role} · ${swapEventLabel(event)}`,
        at: swap.at,
        target: { type: 'event', id: event.id },
      });
    }

    for (const conflict of info.conflicts) {
      const id = `conf-${event.id}-${conflict.member.id}`;
      const lastEdit = state.log.find((l) => l.eventId === event.id);

      out.push({
        id,
        category: 'Conflito',
        tone: 'danger',
        icon: 'alert',
        title: `Conflito · ${conflict.role.name}`,
        text: `${conflict.issue.message} ${event.title}, ${formatShortDate(event.date)}.`,
        at: lastEdit?.at ?? stableAt(id, now),
        target: { type: 'event', id: event.id },
      });
    }
  }

  for (const entry of state.log) {
    if (!entry.text.includes('publicada')) continue;

    const event = state.events.find((e) => e.id === entry.eventId);

    if (!event || event.date < today || !inScope(event.ministryIds)) continue;

    out.push({
      id: `pub-${entry.id}`,
      category: 'Escala',
      tone: 'primary',
      icon: 'send',
      title: 'Escala publicada',
      text: `${event.title} · ${formatShortDate(event.date)} foi publicada por ${entry.who}.`,
      at: entry.at,
      target: { type: 'event', id: event.id },
    });
  }

  const scopedMembers = state.members.filter((m) => isActiveMember(m) && memberInScope(m, scope));

  for (const member of scopedMembers) {
    if (member.availability.every((a) => a === 'unset')) {
      const id = `avail-${member.id}`;

      out.push({
        id,
        category: 'Disponibilidade',
        tone: 'warning',
        icon: 'user',
        title: 'Disponibilidade não configurada',
        text: `${member.name} ainda não informou em quais dias e períodos pode servir.`,
        at: stableAt(id, now),
        target: { type: 'member', id: member.id },
      });
    }

    for (const blackout of member.blackouts) {
      if (blackout.to < today) continue;

      const id = `bo-${blackout.id}`;

      out.push({
        id,
        category: 'Ausência',
        tone: 'success',
        icon: 'plane',
        title: 'Ausência programada',
        text: `${member.name} estará ausente de ${formatDayMonth(blackout.from)} a ${formatDayMonth(blackout.to)}${blackout.reason ? ` · ${blackout.reason}` : ''}.`,
        at: stableAt(id, now),
        target: { type: 'member', id: member.id },
      });
    }
  }

  return out.sort((a, b) => b.at - a.at);
}

/** Agrupa por "Hoje", "Ontem" e "Anteriores". */
export function groupByDay(
  items: readonly AppNotification[],
  now: number = Date.now()
): { label: string; items: AppNotification[] }[] {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const t0 = startOfToday.getTime();
  const groups = [
    { label: 'Hoje', items: items.filter((n) => n.at >= t0) },
    { label: 'Ontem', items: items.filter((n) => n.at < t0 && n.at >= t0 - 24 * HOUR) },
    { label: 'Anteriores', items: items.filter((n) => n.at < t0 - 24 * HOUR) },
  ];

  return groups.filter((g) => g.items.length);
}
